import AjaxWishlistActionBase from './AjaxWishlistActionBase';
import EventBus from '@mvps-genesis/event-bus';

/*
 *--------------------------------------------------------------------------
 * AjaxInsertWishlist
 *--------------------------------------------------------------------------
 *
 * Functionality for asynchronously handling Miva Insert Wishlist (IWSH)
 * actions.
 *
 */
export default class AjaxInsertWishlist extends AjaxWishlistActionBase {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The insert wishlist form element
	 *
	 * @var {HTMLElement}
	 */
	formElement;

	/**
	 * The insert wishlist form submit element
	 *
	 * @var {HTMLElement}
	 */
	submitElement;

	/**
	 * Create a new AjaxInsertWishlist object.
	 *
	 * @param  {HTMLElement|String}  formElement                    The form element.
	 * @param  {Object}              options.actionInput         	The action input object. Defaults to name = Action, value = IWSH.
	 * @param  {HTMLElement|String}  options.loadingElement         The loading UI element. Defaults to '#js-loading-container'.
	 * @param  {HTMLElement|String}  options.loadingOverlayElement  The loading UI overlay element. Defaults to '#js-wishlist-container'.
	 * @param  {HTMLElement|String}  options.messageElement         The message container element. Defaults to '#js-create-wishlist-message'.
	 * @param  {Object}              options.messageOptions         The message configuration options object.
	 * @param  {HTMLElement|String}  options.submitElement          The form submit element. Defaults to '[type="submit"]'.
	 */
	constructor (formElement, {
		actionInput = {
			name: 'Action',
			value: 'IWSH'
		},
		loadingElement = '#js-loading-container',
		loadingOverlayElement = '#js-wishlist-container',
		messageElement = '#js-create-wishlist-message',
		messageOptions = {
			error: {
				dynamic: true,
				message: 'Unable to create list <strong>%wishlistTitle%</strong>.',
				options: {
					autohide: false,
					type: 'error'
				},
				selector: '.g-messages--error'
			},
			success: {
				message: 'Successfully created list <strong>%wishlistTitle%</strong>.',
				options: {
					autohide: false,
					type: 'success'
				},
				selector: '.g-messages--success'
			}
		},
		submitElement = '[type="submit"]'
	} = {}) {
		super({
			actionInput,
			loadingElement,
			loadingOverlayElement,
			messageElement,
			messageOptions
		});

		this.#validator = this.getValidator();

		this.formElement = this.#validator.isElement('formElement', formElement);
		this.submitElement = this.#validator.isElement('submitElement', submitElement, {container: this.formElement});

		this.bindEvents();
	}

	/**
	 * Bind element events.
	 */
	bindEvents () {
		this.formElement.addEventListener('submit', this.onSubmit.bind(this));
	}

	/**
	 * Handle the form submit event.
	 *
	 * @async
	 * @param {event} event The form submit event.
	 */
	// eslint-disable-next-line max-lines-per-function
	async onSubmit (event) {
		event.preventDefault();

		const formData = new FormData(this.formElement);
		const wishlistTitle = formData.get('WishList_Title');

		formData.set(this.actionInput.name, this.actionInput.value);

		const request = new Request(this.formElement.action, {
			body: formData,
			method: this.formElement.method || 'POST'
		});

		let eventDetail = {
			data: {},
			form: this.formElement,
			formData: formData,
			message: '',
			request: request,
			success: false
		};

		this.submitElement.disabled = true;
		this.messageContainer.clear();

		if (this.loadingUI) {
			this.loadingUI.show();
		}

		const response = await fetch(request);

		if (response.ok) {
			const pageCodeResponseHeader = response.headers.get('g-mm-page-code');
			const responseBody = await response.json();

			eventDetail.data = responseBody;

			switch (pageCodeResponseHeader) {
				case 'WISH':
					if (eventDetail.data.wishlist?.id) {
						this.onSuccess(responseBody, wishlistTitle);

						eventDetail.success = true;
					} else {
						this.onFail(responseBody, wishlistTitle);
					}

					break;

				default:
					this.onFail(responseBody, wishlistTitle);
			}

			eventDetail.message = this.messageContainer.currentMessage.message;
		} else {
			this.onFail({}, wishlistTitle);

			eventDetail.message = `${this.constructor.name} - The request failed with a status of: ${response.status}`;
		}

		this.submitElement.disabled = false;

		if (this.loadingUI) {
			this.loadingUI.hide();
		}

		// Dispatch global insertWishlist event
		EventBus.dispatchEvent('MVPS:insertWishlist', eventDetail);
	}

	/**
	 * Handle successful insert wishlist response.
	 *
	 * @param {Object} response      The response body object.
	 * @param {String} wishlistTitle The wishlist title.
	 */
	onSuccess (response, wishlistTitle) {
		this.createMessage(this.messageOptions.success.message.replace('%wishlistTitle%', wishlistTitle), this.messageOptions.success.options);
	}

	/**
	 * Handle failed insert wishlist response
	 *
	 * @param {Object} response      The response body object.
	 * @param {String} wishlistTitle The wishlist title.
	 */
	onFail (response, wishlistTitle) {
		let errorMessage = this.messageOptions.error.message.replace('%wishlistTitle%', wishlistTitle);

		if (this.messageOptions.error.dynamic && response.messages?.error_message_count && response.templates?.messages) {
			const messagesTemplate = new DOMParser().parseFromString(response.templates.messages, 'text/html');
			const errorMessageElement = messagesTemplate.querySelector(this.messageOptions.error.selector);

			if (errorMessageElement) {
				errorMessage = errorMessageElement.innerHTML;
			}
		}

		this.createMessage(errorMessage, this.messageOptions.error.options);
	}

};
